import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiChatAlt2, HiX } from "react-icons/hi";
import AVATAR from "../../assets/inge.png";
import MESSAGE_SOUND from "../../assets/sonido.mp3";

const FloatingAssistant = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const audioRef = useRef(null);
  const isSequenceRunning = useRef(false);
  const cycleTimerRef = useRef(null); // 👈 Referencia para el ciclo

  useEffect(() => {
    audioRef.current = new Audio(MESSAGE_SOUND);
    return () => { audioRef.current = null; };
  }, []);

  const WHATSAPP_NUMBER = "51983974833";
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=Hola! Vengo de ${location.pathname} y quiero asesoría.`;

  const getPageMessages = (pathname) => {
    switch (pathname) {
      case "/planos":
        return ["Hola 👋 soy el asistente virtual.", "Estoy revisando los planos disponibles.", "¿Quieres que te recomiende un plano ideal?"];
      case "/servicios":
        return ["Hola 👋 puedo ayudarte con tu proyecto.", "Realizamos diseño arquitectónico y construcción.", "Asesoría paso a paso."];
      default:
        return ["Hola 👋 soy el asistente virtual.", "¿Necesitas ayuda con tu proyecto?", "Revisa nuestros planos o servicios."];
    }
  };

  // 1. Mostrar al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) setVisible(true);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Función para iniciar el ciclo automático (10s visible → 10s oculto → repite)
  const startCycle = () => {
    clearTimeout(cycleTimerRef.current);

    // Después de 10s visible → minimizar
    cycleTimerRef.current = setTimeout(() => {
      setIsMinimized(true);
      setMessages([]);
      isSequenceRunning.current = false;

      // Después de 10s minimizado → volver a mostrar
      cycleTimerRef.current = setTimeout(() => {
        setIsMinimized(false);
      }, 80000);
    }, 80000);
  };

  // 2. Secuencia de mensajes + arrancar ciclo
  useEffect(() => {
    if (!visible || isMinimized) return;

    const runSequence = async () => {
      if (isSequenceRunning.current) return;
      isSequenceRunning.current = true;

      setMessages([]);
      const msgs = getPageMessages(location.pathname);

      for (let i = 0; i < msgs.length; i++) {
        if (isMinimized) break;
        setTyping(true);
        await new Promise((r) => setTimeout(r, 1200));
        setTyping(false);
        setMessages((prev) => [...prev, msgs[i]]);

        try {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            await audioRef.current.play();
          }
        } catch (err) { }
        await new Promise((r) => setTimeout(r, 1800));
      }

      isSequenceRunning.current = false;
      startCycle(); // ✅ Inicia el ciclo DESPUÉS de que terminan los mensajes
    };

    runSequence();
  }, [visible, isMinimized, location.pathname]);

  // ✅ Al cerrar manualmente: esperar 10s y reaparecer
  const handleClose = () => {
    clearTimeout(cycleTimerRef.current);
    setIsMinimized(true);
    setMessages([]);
    isSequenceRunning.current = false;

    cycleTimerRef.current = setTimeout(() => {
      setIsMinimized(false);
    }, 10000);
  };

  // ✅ Agrega esta función
  const handleReopen = () => {
    clearTimeout(cycleTimerRef.current);
    setIsMinimized(false);
  };

  // Limpiar timers al desmontar
  useEffect(() => {
    return () => clearTimeout(cycleTimerRef.current);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[200] flex flex-col items-end pointer-events-none">

      {/* Mensajes: solo visibles si NO está minimizado */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex flex-col gap-2 mb-4 max-w-[200px] md:max-w-[240px]"
          >
            {messages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white px-4 py-3 rounded-2xl shadow-xl border border-gray-100 text-[13px] text-gray-700 font-medium pointer-events-auto relative"
              >
                {msg}
                {i === messages.length - 1 && (
                  <button
                    onClick={(e) => { e.preventDefault(); handleClose(); }} // ✅ Usa handleClose
                    className="absolute -top-2 -right-2 bg-white shadow-md rounded-full p-1 hover:bg-gray-100 border border-gray-200"
                  >
                    <HiX className="text-xs text-gray-500" />
                  </button>
                )}
              </motion.div>
            ))}
            {typing && (
              <div className="bg-white px-4 py-3 rounded-2xl shadow-lg border border-gray-100 flex gap-1 w-fit">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botón y Avatar */}
      <div className="relative pointer-events-auto cursor-pointer">
        {isMinimized ? (
          // Botón cuando está minimizado: abre el chat
          <button onClick={handleReopen} className="block bg-transparent border-none p-0">
            <motion.div
              animate={{ y: 0, scale: 0.8 }}
              transition={{ duration: 4, repeat: Infinity }}
              className="relative transition-all duration-700 ease-in-out w-14 h-14"
            >
              <div className="absolute inset-0 m-auto flex items-center justify-center scale-110">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-70 animate-ping"></span>
                <div className="relative bg-green-500 p-3 rounded-full border-2 border-white text-white shadow-lg">
                  <HiChatAlt2 size={24} />
                </div>
              </div>
            </motion.div>
          </button>
        ) : (
          // Link a WhatsApp cuando está expandido
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="block">
            <motion.div
              animate={{ y: [0, -8, 0], scale: [1, 1.02, 1] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="relative transition-all duration-700 ease-in-out w-20 h-32 md:w-28 md:h-40"
            >
              <img
                src={AVATAR}
                alt="Ingeniero"
                className="w-full h-full object-contain drop-shadow-xl opacity-100 scale-100 transition-all duration-500"
              />
              <div className="absolute bottom-0 right-0 scale-100">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-70 animate-ping"></span>
                <div className="relative bg-green-500 p-3 rounded-full border-2 border-white text-white shadow-lg">
                  <HiChatAlt2 size={20} />
                </div>
              </div>
            </motion.div>
          </a>
        )}
      </div>
    </div>
  );
};

export default FloatingAssistant;